import Component from '@glimmer/component';

interface DashboardStatSignature {
  Args: {
    title: string;
    value: { count: number; more: boolean };
    /** Shown instead of `value.count` when the stat is not a plain number. */
    display?: string;
    detail?: string;
    icon: string;
    /** Semantic treatment; meaning is also carried by title, value, and icon. */
    variant?: 'neutral' | 'attention';
    /** Tighter padding and a smaller value, for use beside a name card. */
    compact?: boolean;
  };
  Element: HTMLDivElement;
}

export class DashboardStat extends Component<DashboardStatSignature> {
  get needsAttention() {
    return this.args.variant === 'attention' && this.args.value.count > 0;
  }

  get cardClass() {
    if (this.needsAttention) {
      return 'border border-warning border-2 text-body-emphasis';
    }

    return 'border-0 text-bg-primary';
  }

  get iconClass() {
    if (this.needsAttention) {
      return 'text-bg-warning';
    }

    return 'bg-white bg-opacity-25';
  }

  /**
   * Phrase stats (e.g. "Session upcoming") need a slightly smaller type and
   * normal line-height so they wrap on the space, not mid-word, in a narrow card.
   */
  get valueSizeClass() {
    if (this.args.display) {
      return this.args.compact
        ? 'fs-4 lh-sm text-break'
        : 'll-stat-value ll-stat-value-phrase lh-sm text-break';
    }

    return this.args.compact ? 'fs-2 lh-1' : 'll-stat-value lh-1';
  }

  <template>
    <div
      class="card shadow-sm rounded-4 overflow-hidden {{this.cardClass}}"
      ...attributes
    >
      <div
        class="card-body d-flex flex-column h-100
          {{if @compact 'p-3' 'p-3 p-lg-4'}}"
      >
        <div
          class="d-flex justify-content-between align-items-start gap-2 min-w-0
            {{if @compact 'mb-2' 'mb-3'}}"
        >
          <p class="gud-eyebrow mb-0 min-w-0">{{@title}}</p>
          <span
            class="rounded-3
              {{this.iconClass}}
              d-none d-sm-inline-flex align-items-center justify-content-center p-2 flex-shrink-0"
            aria-hidden="true"
          >
            <iconify-icon
              inline
              icon={{@icon}}
              class={{if @compact "fs-6" "fs-5"}}
            />
          </span>
        </div>
        <p
          class="fw-bold text-break
            {{this.valueSizeClass}}
            {{if @detail 'mb-2' 'mb-0'}}"
        >
          {{#if @display}}
            {{@display}}
          {{else}}
            {{@value.count}}{{if @value.more "+"}}
          {{/if}}
        </p>
        {{#if @detail}}
          <p class="small mb-0 mt-auto text-break">
            {{@detail}}
          </p>
        {{/if}}
      </div>
    </div>
  </template>
}
